import {
  GridColumnVisibilityModel,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid-premium';

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

import { FilterValues } from '@/shared/ui/GeneratikTable/GeneratikTableFilterPanel.types';

export interface GeneratikTableState {
  tableId: string;
  columnVisibilityModel: GridColumnVisibilityModel;
  sortModel: GridSortModel;
  filterModel: GridFilterModel;
  paginationModel: GridPaginationModel;
  groupByField: string | null;
  searchValue: string;
  viewMode: 'grid' | 'list';
  filterValues: FilterValues;
  variant: 'data' | 'timeline';
  timelineGranularity: 'week' | 'month';
}

export const DEFAULT_TABLE_STATE: Omit<GeneratikTableState, 'tableId'> = {
  columnVisibilityModel: {},
  sortModel: [],
  filterModel: { items: [] },
  paginationModel: { page: 0, pageSize: 25 },
  groupByField: null,
  searchValue: '',
  viewMode: 'grid',
  filterValues: {},
  variant: 'data',
  timelineGranularity: 'week',
};

export type TableScrollView = 'grid' | 'list';

export interface GeneratikTableStore {
  tables: Record<string, GeneratikTableState>;
  // Kept out of `partialize` on purpose: the collapsed buckets follow the user across in-app
  // navigation but a fresh page load starts with every bucket open again (AAD-3008, AAD-3320).
  collapsedGroupIds: Record<string, string[]>;
  collapsedListGroups: Record<string, string[]>;
  scrollPositions: Record<string, Partial<Record<TableScrollView, number>>>;
  selectedRows: Record<string, unknown[]>;
  columnWidths: Record<string, Record<string, number>>;
  zoomLevels: Record<string, number>;

  // Initialize or get table state
  initTable: (
    tableId: string,
    initialState?: Partial<Omit<GeneratikTableState, 'tableId'>>,
  ) => void;

  // Getters
  getTableState: (tableId: string) => GeneratikTableState | undefined;

  // Setters
  setColumnVisibilityModel: (tableId: string, model: GridColumnVisibilityModel) => void;
  setSortModel: (tableId: string, model: GridSortModel) => void;
  setFilterModel: (tableId: string, model: GridFilterModel) => void;
  setPaginationModel: (tableId: string, model: GridPaginationModel) => void;
  setGroupByField: (tableId: string, field: string | null) => void;
  setCollapsedGroupIds: (tableId: string, ids: string[]) => void;
  setCollapsedListGroups: (tableId: string, labels: string[]) => void;
  setScrollPosition: (tableId: string, view: TableScrollView, top: number) => void;
  setSearchValue: (tableId: string, value: string) => void;
  setViewMode: (tableId: string, mode: 'grid' | 'list') => void;
  setVariant: (tableId: string, variant: 'data' | 'timeline') => void;
  setTimelineGranularity: (tableId: string, granularity: 'week' | 'month') => void;
  setFilterValues: (tableId: string, values: FilterValues) => void;
  setSelectedRows: (tableId: string, rows: unknown[]) => void;
  removeSelectedRow: (tableId: string, rowId: string) => void;
  setColumnWidth: (tableKey: string, field: string, width: number) => void;
  setColumnWidths: (tableKey: string, widths: Record<string, number>) => void;
  clearColumnWidths: (tableKey: string) => void;
  setZoomLevel: (key: string, level: number) => void;

  // Bulk update
  updateTableState: (tableId: string, state: Partial<Omit<GeneratikTableState, 'tableId'>>) => void;

  // Reset
  resetTableState: (tableId: string) => void;
  clearAllTables: () => void;
}

export const buildColumnWidthKey = (pageType: string, resourceId?: string): string =>
  resourceId ? `${pageType}:${resourceId}` : pageType;

const useGeneratikTableStore = create<GeneratikTableStore>()(
  devtools(
    persist(
      (set, get) => ({
        tables: {},
        collapsedGroupIds: {},
        collapsedListGroups: {},
        scrollPositions: {},
        selectedRows: {},
        columnWidths: {},
        zoomLevels: {},

        initTable: (
          tableId: string,
          initialState?: Partial<Omit<GeneratikTableState, 'tableId'>>,
        ) => {
          const existingState = get().tables[tableId];
          if (!existingState) {
            set(
              (state) => ({
                tables: {
                  ...state.tables,
                  [tableId]: {
                    tableId,
                    ...DEFAULT_TABLE_STATE,
                    ...initialState,
                  },
                },
              }),
              undefined,
              'initTable',
            );
          }
        },

        getTableState: (tableId: string) => {
          return get().tables[tableId];
        },

        setColumnVisibilityModel: (tableId: string, model: GridColumnVisibilityModel) =>
          set(
            (state) => {
              const tableState = state.tables[tableId];
              if (!tableState) return state;

              return {
                tables: {
                  ...state.tables,
                  [tableId]: {
                    ...tableState,
                    columnVisibilityModel: model,
                  },
                },
              };
            },
            undefined,
            'setColumnVisibilityModel',
          ),

        setSortModel: (tableId: string, model: GridSortModel) =>
          set(
            (state) => {
              const tableState = state.tables[tableId];
              if (!tableState) return state;

              return {
                tables: {
                  ...state.tables,
                  [tableId]: {
                    ...tableState,
                    sortModel: model,
                  },
                },
              };
            },
            undefined,
            'setSortModel',
          ),

        setFilterModel: (tableId: string, model: GridFilterModel) =>
          set(
            (state) => {
              const tableState = state.tables[tableId];
              if (!tableState) return state;

              return {
                tables: {
                  ...state.tables,
                  [tableId]: {
                    ...tableState,
                    filterModel: model,
                  },
                },
              };
            },
            undefined,
            'setFilterModel',
          ),

        setPaginationModel: (tableId: string, model: GridPaginationModel) =>
          set(
            (state) => {
              const tableState = state.tables[tableId];
              if (!tableState) return state;

              return {
                tables: {
                  ...state.tables,
                  [tableId]: {
                    ...tableState,
                    paginationModel: model,
                  },
                },
              };
            },
            undefined,
            'setPaginationModel',
          ),

        setGroupByField: (tableId: string, field: string | null) =>
          set(
            (state) => {
              const tableState = state.tables[tableId];
              if (!tableState) return state;

              return {
                tables: {
                  ...state.tables,
                  [tableId]: {
                    ...tableState,
                    groupByField: field,
                  },
                },
              };
            },
            undefined,
            'setGroupByField',
          ),

        setCollapsedGroupIds: (tableId: string, ids: string[]) =>
          set(
            (state) => ({
              collapsedGroupIds: { ...state.collapsedGroupIds, [tableId]: ids },
            }),
            undefined,
            'setCollapsedGroupIds',
          ),

        setCollapsedListGroups: (tableId: string, labels: string[]) =>
          set(
            (state) => ({
              collapsedListGroups: { ...state.collapsedListGroups, [tableId]: labels },
            }),
            undefined,
            'setCollapsedListGroups',
          ),

        setScrollPosition: (tableId: string, view: TableScrollView, top: number) =>
          set(
            (state) => ({
              scrollPositions: {
                ...state.scrollPositions,
                [tableId]: { ...state.scrollPositions[tableId], [view]: top },
              },
            }),
            undefined,
            'setScrollPosition',
          ),

        setSearchValue: (tableId: string, value: string) =>
          set(
            (state) => {
              const tableState = state.tables[tableId];
              if (!tableState) return state;

              return {
                tables: {
                  ...state.tables,
                  [tableId]: {
                    ...tableState,
                    searchValue: value,
                  },
                },
              };
            },
            undefined,
            'setSearchValue',
          ),

        setViewMode: (tableId: string, mode: 'grid' | 'list') =>
          set(
            (state) => {
              const tableState = state.tables[tableId];
              if (!tableState) return state;

              return {
                tables: {
                  ...state.tables,
                  [tableId]: {
                    ...tableState,
                    viewMode: mode,
                  },
                },
              };
            },
            undefined,
            'setViewMode',
          ),

        setVariant: (tableId: string, variant: 'data' | 'timeline') =>
          set(
            (state) => {
              const tableState = state.tables[tableId];
              if (!tableState) return state;

              return {
                tables: {
                  ...state.tables,
                  [tableId]: {
                    ...tableState,
                    variant,
                  },
                },
              };
            },
            undefined,
            'setVariant',
          ),

        setTimelineGranularity: (tableId: string, granularity: 'week' | 'month') =>
          set(
            (state) => {
              const tableState = state.tables[tableId];
              if (!tableState) return state;

              return {
                tables: {
                  ...state.tables,
                  [tableId]: {
                    ...tableState,
                    timelineGranularity: granularity,
                  },
                },
              };
            },
            undefined,
            'setTimelineGranularity',
          ),

        setFilterValues: (tableId: string, values: FilterValues) =>
          set(
            (state) => {
              const tableState = state.tables[tableId];
              if (!tableState) return state;

              const filtersChanged =
                JSON.stringify(tableState.filterValues ?? {}) !== JSON.stringify(values ?? {});
              return {
                tables: {
                  ...state.tables,
                  [tableId]: {
                    ...tableState,
                    filterValues: values,
                    paginationModel:
                      filtersChanged && tableState.paginationModel
                        ? { ...tableState.paginationModel, page: 0 }
                        : tableState.paginationModel,
                  },
                },
              };
            },
            undefined,
            'setFilterValues',
          ),

        setSelectedRows: (tableId: string, rows: unknown[]) =>
          set(
            (state) => {
              return {
                selectedRows: { ...state.selectedRows, [tableId]: rows },
              };
            },
            undefined,
            'setSelectedRows',
          ),

        removeSelectedRow: (tableId: string, rowId: string) =>
          set(
            (state) => ({
              selectedRows: {
                ...state.selectedRows,
                [tableId]: (state.selectedRows[tableId] ?? []).filter(
                  (row) => (row as { id: string }).id !== rowId,
                ),
              },
            }),
            undefined,
            'removeSelectedRow',
          ),

        setColumnWidth: (tableKey: string, field: string, width: number) =>
          set(
            (state) => ({
              columnWidths: {
                ...state.columnWidths,
                [tableKey]: {
                  ...(state.columnWidths[tableKey] ?? {}),
                  [field]: width,
                },
              },
            }),
            undefined,
            'setColumnWidth',
          ),

        setColumnWidths: (tableKey: string, widths: Record<string, number>) =>
          set(
            (state) => ({
              columnWidths: { ...state.columnWidths, [tableKey]: widths },
            }),
            undefined,
            'setColumnWidths',
          ),

        clearColumnWidths: (tableKey: string) =>
          set(
            (state) => {
              const next = { ...state.columnWidths };
              delete next[tableKey];
              return { columnWidths: next };
            },
            undefined,
            'clearColumnWidths',
          ),

        setZoomLevel: (key: string, level: number) =>
          set(
            (state) => ({
              zoomLevels: { ...state.zoomLevels, [key]: level },
            }),
            undefined,
            'setZoomLevel',
          ),

        updateTableState: (
          tableId: string,
          newState: Partial<Omit<GeneratikTableState, 'tableId'>>,
        ) =>
          set(
            (state) => {
              const tableState = state.tables[tableId];
              if (!tableState) return state;

              return {
                tables: {
                  ...state.tables,
                  [tableId]: {
                    ...tableState,
                    ...newState,
                  },
                },
              };
            },
            undefined,
            'updateTableState',
          ),

        resetTableState: (tableId: string) =>
          set(
            (state) => {
              const tableState = state.tables[tableId];
              if (!tableState) return state;

              const { [tableId]: _reset, ...collapsedGroupIds } = state.collapsedGroupIds;
              const { [tableId]: _resetList, ...collapsedListGroups } = state.collapsedListGroups;
              const { [tableId]: _resetScroll, ...scrollPositions } = state.scrollPositions;
              return {
                tables: {
                  ...state.tables,
                  [tableId]: {
                    tableId,
                    ...DEFAULT_TABLE_STATE,
                  },
                },
                collapsedGroupIds,
                collapsedListGroups,
                scrollPositions,
              };
            },
            undefined,
            'resetTableState',
          ),

        clearAllTables: () =>
          set(
            () => ({
              tables: {},
              collapsedGroupIds: {},
              collapsedListGroups: {},
              scrollPositions: {},
              selectedRows: {},
              columnWidths: {},
              zoomLevels: {},
            }),
            undefined,
            'clearAllTables',
          ),
      }),
      {
        name: 'generatik-table-storage',
        partialize: (state) => ({
          tables: state.tables,
          selectedRows: state.selectedRows,
          columnWidths: state.columnWidths,
          zoomLevels: state.zoomLevels,
        }),
      },
    ),
    {
      name: 'GeneratikTableStore',
      enabled: true,
    },
  ),
);

export default useGeneratikTableStore;
